import { RefObject, useCallback, useEffect, useLayoutEffect } from 'react';

/** Whether the browser sizes a textarea to its content on its own (CSS `field-sizing: content`). */
export function supportsFieldSizing(): boolean {
  return typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('field-sizing', 'content');
}

/**
 * Size a textarea to its content, between its CSS `min-height` and `max-height`.
 *
 * Give the textarea `field-sizing: content` (Tailwind `field-sizing-content`)
 * plus a min and max height: browsers that support it grow it with no JS, and
 * past the max it scrolls internally. Elsewhere (Safari < 26, Firefox) this
 * does the same by hand — reset to `auto`, then set the height from
 * `scrollHeight` (the CSS max still caps it) — whenever `value` changes or the
 * window resizes (a narrower box wraps to more lines). Programmatic changes
 * (an inserted draft, a translation, clearing after send) all go through
 * `value`, so they resize too.
 */
export function useAutoGrowTextarea(ref: RefObject<HTMLTextAreaElement | null>, value: string) {
  const resize = useCallback(() => {
    const el = ref.current;
    if (!el || supportsFieldSizing()) return;
    el.style.height = 'auto';
    const cs = window.getComputedStyle(el);
    const borders = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    el.style.height = `${el.scrollHeight + borders}px`;
  }, [ref]);

  useLayoutEffect(resize, [resize, value]);

  useEffect(() => {
    if (supportsFieldSizing()) return;
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [resize]);
}
